import { useState, useRef, useEffect } from 'react'
import { Wordmark } from '@/components/wordmark'
import { Link, useRouterState } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/hooks/use-auth'
import { ChevronDown, Menu, X } from 'lucide-react'
import { sectionOf } from '@/lib/nav-section'
import { compareMenu, monitoringMenu, toolsMenu, type Menu as MenuData } from '@/components/site-menus'

// --- Navbar B: links at full contrast; the current page gets a 2 px accent underline ---

// The underline is an `after:` line that only changes opacity, so nothing shifts.
// `-bottom-[13px]` puts it on the lower edge of the 72 px bar.
const navItem =
  'relative rounded-md px-3.5 py-2 text-base font-medium text-foreground/90 transition-colors hover:text-foreground aria-[current=page]:text-foreground ' +
  'after:pointer-events-none after:absolute after:inset-x-3.5 after:-bottom-[13px] after:h-0.5 after:rounded-full after:bg-primary ' +
  'after:opacity-0 aria-[current=page]:after:opacity-100'

// Phone menu: the current link gets a 2 px left border in `primary` instead. The
// border sits in the card padding, so the text keeps its place.
const mobileItem =
  '-ml-3 border-l-2 border-transparent pl-[10px] text-foreground/90 hover:text-foreground aria-[current=page]:border-primary aria-[current=page]:text-foreground'

// The phone menu lists the busiest tools and a link to the rest.
const PHONE_TOOLS = ['/tools/whois-lookup', '/tools/dns-lookup', '/tools/ssl-checker', '/tools/is-website-down', '/tools/browser-check', '/tools']

// --- Components ---

function MegaDropdown({ menu, open, inSection, onToggle, onClose }: { menu: MenuData; open: boolean; inSection: boolean; onToggle: () => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, onClose])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-current={inSection ? 'page' : undefined}
        className={`flex items-center gap-1.5 ${navItem} ${open ? 'bg-accent' : ''}`}
      >
        {menu.label}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 rounded-lg border bg-popover p-6 shadow-md">
          <div className="flex gap-10">
            {menu.columns.map((col) => (
              <div key={col.title} className="min-w-[180px]">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {col.title}
                </p>
                <ul className="space-y-1">
                  {col.items.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        onClick={onClose}
                        className="group flex items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent"
                      >
                        <div>
                          <span className="flex items-center gap-1.5 font-medium text-foreground">
                            {item.label}
                            {item.badge && (
                              <Badge variant="secondary" className="px-1.5 py-0 text-xs leading-4">
                                {item.badge}
                              </Badge>
                            )}
                          </span>
                          {item.description && (
                            <span className="text-xs text-muted-foreground">{item.description}</span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function SiteHeader() {
  const { isAuthenticated, isLoading } = useAuth()
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const section = sectionOf(useRouterState({ select: (state) => state.location.pathname }))

  function toggle(label: string) {
    setOpenMenu((prev) => (prev === label ? null : label))
  }

  function close() {
    setOpenMenu(null)
    setMobileOpen(false)
  }

  return (
    <header className="sticky top-0 z-50 pt-3">
      <div className="container mx-auto px-4">
      {/* A floating bar: logo left, links centred, account actions right. */}
      <div className="grid h-[72px] grid-cols-[auto_1fr_auto] items-center gap-6 rounded-lg border border-border bg-card px-6 shadow-raised">
          <Link to="/" className="flex items-center" onClick={close} aria-label="Uptrack home">
            <Wordmark height={32} />
          </Link>
          <nav aria-label="Main" className="hidden items-center justify-center gap-2 text-base md:flex">
            <MegaDropdown menu={monitoringMenu} open={openMenu === 'Monitoring'} inSection={section === 'Monitoring'} onToggle={() => toggle('Monitoring')} onClose={close} />
            <MegaDropdown menu={toolsMenu} open={openMenu === 'Tools'} inSection={section === 'Tools'} onToggle={() => toggle('Tools')} onClose={close} />
            <MegaDropdown menu={compareMenu} open={openMenu === 'Compare'} inSection={section === 'Compare'} onToggle={() => toggle('Compare')} onClose={close} />
            <Link to="/pricing" onClick={close} className={navItem}>
              Pricing
            </Link>
            <Link to="/blog" onClick={close} className={navItem}>
              Blog
            </Link>
          </nav>
        <div className="col-start-3 flex items-center gap-3">
          <nav aria-label="Account" className="flex items-center gap-3">
            {isLoading ? (
              <div className="h-11 w-24 animate-pulse rounded bg-muted" />
            ) : isAuthenticated ? (
              <Button size="lg" asChild>
                <Link to="/dashboard" onClick={close}>Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="lg" asChild className="hidden text-base sm:inline-flex">
                  <Link to="/login" onClick={close}>Sign In</Link>
                </Button>
              </>
            )}
          </nav>
          {/* Mobile hamburger */}
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="mt-2 rounded-lg border border-border bg-card px-4 py-4 shadow-raised md:hidden">
          <nav aria-label="Mobile" className="flex flex-col gap-3 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monitoring</span>
            {monitoringMenu.columns.flatMap((col) =>
              col.items.slice(0, 3).map((item) => (
                <Link key={item.label} to={item.href} onClick={close} className={mobileItem}>
                  {item.label}
                </Link>
              ))
            )}
            <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tools</span>
            {toolsMenu.columns.flatMap((col) => col.items).filter((item) => PHONE_TOOLS.includes(item.href)).map((item) => (
              <Link key={item.label} to={item.href} onClick={close} className={mobileItem}>
                {item.label}
              </Link>
            ))}
            <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Compare</span>
            {compareMenu.columns[0].items.map((item) => (
              <Link key={item.label} to={item.href} onClick={close} className={mobileItem}>
                {item.label}
              </Link>
            ))}
            <Link to="/pricing" onClick={close} className={`mt-2 font-medium ${mobileItem}`}>
              Pricing
            </Link>
            {!isAuthenticated && (
              <Link to="/login" onClick={close} className={mobileItem}>
                Sign In
              </Link>
            )}
          </nav>
        </div>
      )}
      </div>
    </header>
  )
}
